import useStyles from 'isomorphic-style-loader/useStyles';
import { ExternalLink } from 'lucide-react';
import type { ReactNode } from 'react';
import { Popup } from 'semantic-ui-react';

import s from './anchore-link.scss';

const ANCHORE_VULN_BASE_URL = 'https://vulnerability.anchore.io';

export const getAnchoreVulnUrl = (vulnerabilityId: string): string =>
  `${ANCHORE_VULN_BASE_URL}/vuln/${encodeURIComponent(vulnerabilityId)}`;

/**
 * The Anchore wordmark, colored via `currentcolor`. Exposed so callers can
 * place it within their own copy (e.g. an inline sentence around the link).
 */
export const AnchoreWordmark = () => {
  useStyles(s);

  return <span role="img" aria-label="Anchore" className={s.logo} />;
};

export interface AnchoreLinkProps {
  vulnerabilityId: string;
  /**
   * Inline link content; when set, the accessible name comes from this visible
   * content and the hover popup is omitted (otherwise a compact wordmark-only
   * link with the popup is rendered).
   */
  children?: ReactNode;
}

/**
 * A link to the Anchore vulnerability page for the given vulnerability ID.
 * Renders the Anchore wordmark and an external-link icon, both colored via
 * `currentcolor` so they share the surrounding link tint.
 */
export const AnchoreLink = ({
  vulnerabilityId,
  children,
}: AnchoreLinkProps) => {
  useStyles(s);

  const link = (
    <a
      className={s.root}
      href={getAnchoreVulnUrl(vulnerabilityId)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={children ? undefined : `View ${vulnerabilityId} on Anchore`}
    >
      {children ?? <AnchoreWordmark />}
      <ExternalLink className={s.icon} />
    </a>
  );

  if (children) {
    return link;
  }

  return (
    <Popup
      content="Go to vulnerability details"
      position="right center"
      size="mini"
      trigger={link}
    />
  );
};
